/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import { Card, Tooltip, Icon } from '@scuf/common';
import descriptors from '../../../../../../locales/en-US/descriptors';
import {
  IDetailComponentProps,
  IEmaasEnergyUsageComponentData
} from '../../../../../DashboardKPIWidgets/IEnergyManagementProps';
import ConnectionError from '../../../../../shared/ErrorComponents/ConnectionError';
import withErrorStatus from '../../../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../../../shared/StatusWrappers/LoadingStatus';
import { isObjectOrArrayAndEmpty } from '../../../../../shared/Utils/Utils';
import PortfolioEnergyTopComponent from './PortfolioEnergyTopComponent';
import { GetHeader } from '../../../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType,
  EnergyUsageField
} from '../../../../../shared/Enums';
import EmaasEUsageAllUtilDetailsBasicComponent from '../../../../EnergyUsagesViewDetailsWidget/AllUtilitiesDetails/EmaasEUsageAllUtilDetailsBasicComponent';
import { ApiContext } from '../../../../../../context/ApiContext';

interface ITotalEmissionTabDetailsComponentProps {
  data: IEmaasEnergyUsageComponentData;
  onRefresh: () => Promise<void>;
  tabName?: string;
  forSummary?: boolean;
  forGraph?: boolean;
  checkAllSitesAndMonthData?: boolean;
  dataSourceType?: string;
}
const PortfolioEnergyAllUtilitiesDetailContent = (
  props: ITotalEmissionTabDetailsComponentProps
) => {
  const { data, onRefresh, checkAllSitesAndMonthData } = props;
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          onRefresh={onRefresh}
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
        />
      </div>
    );
  }
  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        {data.subscriptionType === EnergyUsageField.BASICSUBS ? (
          <EmaasEUsageAllUtilDetailsBasicComponent
            data={data}
            checkAllSitesAndMonthData={checkAllSitesAndMonthData}
            dataSourceType={props.dataSourceType}
          />
        ) : (
          <PortfolioEnergyTopComponent
            tabName={props.tabName}
            forGraph={props.forGraph}
            forSummary={props.forSummary}
            data={data}
          />
        )}
      </div>
    </div>
  );
};

const PortfolioEnergyAllUtilitiesDetailWithLoader = withLoadingStatus(
  withErrorStatus(PortfolioEnergyAllUtilitiesDetailContent)
);

const PortfolioEnergyTopWidgetHOC = (props: IDetailComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { dataConnector, tabName, requestContext } = props;

  const apiContext = useContext(ApiContext);
  const { dataSourceType } = apiContext;

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const electricitySavings = (all as unknown) as IEmaasEnergyUsageComponentData;
  const isDataAvailable = electricitySavings ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IEmaasEnergyUsageComponentData);
  const [checkAllSitesAndMonthData, setCheckAllSitesAndMonthData] = useState<
    boolean
  >(true);

  const checkIfAllSitesHasCompleteData = (data: any) => {
    return (
      data &&
      data.breakdownBySite &&
      (data.subscriptionType !== EnergyUsageField.BASICSUBS ||
        data.breakdownBySite.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthHasCompleteData = (data: any) => {
    return (
      data &&
      data.monthWiseAggregations &&
      (data.subscriptionType !== EnergyUsageField.BASICSUBS ||
        data.monthWiseAggregations.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthsHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data.monthWiseAggregations &&
      data.monthWiseAggregations.every(
        (item: {
          totalBaselineConsumption: number | null;
          totalConsumption: number | null;
        }) =>
          item.totalBaselineConsumption !== 0 &&
          item.totalBaselineConsumption !== null &&
          item.totalConsumption !== 0 &&
          item.totalConsumption !== null
      )
    );
  };

  const checkIfAllSitesHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data.breakdownBySite &&
      data.breakdownBySite.every(
        (item: {
          totalBaselineConsumption: number | null;
          totalConsumption: number | null;
        }) =>
          item.totalConsumption !== 0 &&
          item.totalBaselineConsumption !== 0 &&
          item.totalConsumption !== null &&
          item.totalBaselineConsumption !== null
      )
    );
  };

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setCheckAllSitesAndMonthData(
          checkIfAllSitesHasPreviousPeriod(electricitySavings) &&
            checkIfAllMonthsHasPreviousPeriod(electricitySavings) &&
            checkIfAllMonthHasCompleteData(electricitySavings) &&
            checkIfAllSitesHasCompleteData(electricitySavings)
        );
        setData(electricitySavings);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);
  const header = GetHeader(tabName);
  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header>
          <div className="emaas-summary-card-header">
            <span>{t(header)}</span>
            <Tooltip
              element={
                <Icon
                  root="common"
                  name="badge-info"
                  exactSize="0.875rem"
                  className="emaas-summary-card-header-info-icon"
                />
              }
              event="hover"
              content={
                tabName === 'allutilities'
                  ? data.subscriptionType === EnergyUsageField.BASICSUBS ||
                    dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                    ? t(d.CEMSPORTFOLIO_DATA_SOURCE_UTIL_TOOLTIP)
                    : t(d.EMAASPORTFOLIOENERGYUSAGETOOLTIP)
                  : data.subscriptionType === EnergyUsageField.BASICSUBS ||
                    dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                  ? t(d.CEMSPORTFOLIO_DATA_SOURCE_UTIL_TOOLTIP)
                  : tabName === 'water'
                  ? t(d.EMAASWATEROVERVIEWTOOLTIP)
                  : t(d.EMAASENERGYUSAGETOOLTIP)
              }
              position="right center"
              size="small"
              header={t(header)}
            />
          </div>
        </Card.Header>
        <Card.Content className="detail-summary-card">
          <PortfolioEnergyAllUtilitiesDetailWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            tabName={tabName}
            forSummary={true}
            forGraph={true}
            checkAllSitesAndMonthData={checkAllSitesAndMonthData}
            dataSourceType={dataSourceType ? dataSourceType : ''}
          />
        </Card.Content>
      </Card>
    </>
  );
};
export default PortfolioEnergyTopWidgetHOC;
